import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, PageHeaderTab } from "../../components/PageHeader";
import { ListToolbar } from "../../components/ListToolbar";
import { Modal } from "../../components/Modal";
import { ImportNoticesModal } from "./ImportNoticesModal";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import {
  ApiError,
  createConfirmingPayment,
  deleteConfirmingPayment,
  getConfirmingHistoryBySupplier,
  getConfirmingPayments,
  getConfirmingPlafond,
  getConfirmingBanks,
  getConfirmingSettings,
  getEligibleSupplierInvoices,
  updateConfirmingPaymentDetails,
  updateConfirmingPaymentStatus,
  type ConfirmingBank,
  type ConfirmingPayment,
  type ConfirmingPaymentFilters,
  type ConfirmingPlafond,
  type ConfirmingSettings,
  type EligibleSupplierInvoice,
  type SupplierConfirmingHistory,
} from "../../confirming/confirmingApi";

type TabId = "aps" | "settlements" | "history";

// Os 5 estados sedeados (§1.2). Definidos aqui para o dropdown de ação e as badges —
// nunca por Id (o classificador é o Code).
const STATUS_OPTIONS: { code: string; name: string }[] = [
  { code: "pendente", name: "Pendente" },
  { code: "confirmada", name: "Confirmada" },
  { code: "paga", name: "Paga ao fornecedor" },
  { code: "liquidada", name: "Liquidada" },
  { code: "anulada", name: "Anulada" },
];

// Cor por estado (aplicada ao dropdown de estado) — classes já compiladas em index.css.
const STATUS_BADGE: Record<string, string> = {
  pendente: "bg-yellow-500/10 text-yellow-700",
  confirmada: "bg-primary/10 text-primary",
  paga: "bg-green-500/10 text-green-700",
  liquidada: "bg-muted text-secondary-text",
  anulada: "bg-destructive/10 text-destructive",
};

// Por liquidar: ocupam plafond e ainda se acertam (banco, datas).
const ACTIVE_STATUS_CODES = ["pendente", "confirmada", "paga"];

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

const parseNumber = (value: string): number | null => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const todayInput = (): string => new Date().toISOString().slice(0, 10);

/** "AAAA-MM-DD" + n dias, em "AAAA-MM-DD" (vazio se a data não for válida). */
const addDaysInput = (date: string, days: number): string => {
  const base = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(base.getTime())) return "";
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
};

/**
 * Nº do Aviso de Pagamento na linha do AP: o banco dá-o depois de o AP existir. Escreve-se aqui e
 * grava ao sair do campo; é ele que liga sozinho o débito da liquidação na reconciliação bancária.
 */
function ApReferenceInput({
  ap,
  disabled,
  onSave,
}: {
  ap: ConfirmingPayment;
  disabled: boolean;
  onSave: (ap: ConfirmingPayment, reference: string | null) => Promise<boolean>;
}) {
  const [value, setValue] = useState(ap.reference ?? "");
  useEffect(() => setValue(ap.reference ?? ""), [ap.reference]);

  return (
    <input
      type="text"
      value={value}
      disabled={disabled}
      placeholder="Nº do aviso"
      aria-label={`Nº do aviso de ${ap.supplierName}`}
      onChange={(event) => setValue(event.target.value)}
      onBlur={async () => {
        const next = value.trim() || null;
        if (next === (ap.reference ?? null)) return;
        // Não gravou (outra linha ocupada, erro): volta ao que está guardado, para não enganar.
        if (!(await onSave(ap, next))) setValue(ap.reference ?? "");
      }}
      className="mt-1 w-full px-2 py-1 border border-border bg-background text-foreground text-xs disabled:opacity-50"
      style={{ borderRadius: "var(--radius)" }}
    />
  );
}

// Custo financeiro pré-calculado no cliente (espelha ConfirmingCostCalculator, §2):
// termDays = max(0, dias inteiros issue→due); cost = round(amount * rate/100 * termDays/365, 2).
const computeCost = (
  amount: number,
  annualRatePercent: number,
  issue: string,
  due: string,
): { termDays: number; financialCost: number } => {
  const issueDate = new Date(`${issue}T00:00:00`);
  const dueDate = new Date(`${due}T00:00:00`);
  if (
    Number.isNaN(issueDate.getTime()) ||
    Number.isNaN(dueDate.getTime()) ||
    amount <= 0
  ) {
    return { termDays: 0, financialCost: 0 };
  }
  const msPerDay = 1000 * 60 * 60 * 24;
  const termDays = Math.max(
    0,
    Math.round((dueDate.getTime() - issueDate.getTime()) / msPerDay),
  );
  const raw = amount * (annualRatePercent / 100) * (termDays / 365);
  const financialCost = Math.round((raw + Number.EPSILON) * 100) / 100;
  return { termDays, financialCost };
};


function StatTile({
  label,
  value,
  sub,
  valueColor,
}: {
  label: string;
  value: string;
  sub?: string;
  valueColor?: string;
}) {
  return (
    <div className="bg-muted/30 p-3" style={{ borderRadius: "var(--radius)" }}>
      <div className="text-xs text-secondary-text">{label}</div>
      <div
        className="text-xl mt-1"
        style={{
          fontWeight: "var(--font-weight-semi-bold)",
          color: valueColor,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-secondary-text mt-1">{sub}</div>}
    </div>
  );
}

// Widget de plafond [3.4.3] — Limite / Usado / Disponível + barra de progresso, em tempo real.
function PlafondWidget({ plafond }: { plafond: ConfirmingPlafond | null }) {
  if (!plafond) {
    return (
      <div
        className="bg-card border border-border p-4"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <span className="text-secondary-text text-sm">A carregar plafond…</span>
      </div>
    );
  }
  const pct =
    plafond.limit > 0
      ? Math.min(100, Math.max(0, (plafond.used / plafond.limit) * 100))
      : 0;
  const over = plafond.available < 0;
  const near = !over && pct >= 90;
  const successColor = "var(--color-success, #16a34a)";
  const barColor = over
    ? "var(--destructive)"
    : near
      ? "var(--color-warning, #d97706)"
      : successColor;

  return (
    <div
      className="bg-card border border-border p-4 space-y-3"
      style={{ borderRadius: "var(--radius-card)" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatTile label="Limite" value={formatCurrency(plafond.limit)} />
        <StatTile
          label="Usado"
          value={formatCurrency(plafond.used)}
          sub={`${plafond.activeCount} AP(s) a consumir plafond`}
        />
        <StatTile
          label="Disponível"
          value={formatCurrency(plafond.available)}
          valueColor={over ? "var(--destructive)" : successColor}
        />
      </div>
      <div
        className="bg-muted overflow-hidden"
        style={{ height: 8, borderRadius: 9999 }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: barColor,
            transition: "width 0.3s ease",
          }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-secondary-text flex-wrap gap-2">
        <span>{pct.toFixed(0)}% do plafond utilizado</span>
        <span>
          Custo financeiro acumulado {formatCurrency(plafond.totalFinancialCost)}
        </span>
      </div>
      {(plafond.banks?.length ?? 0) > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {plafond.banks!.map((bank) => (
            <div
              key={bank.bankId ?? "sem-banco"}
              className="flex items-center justify-between gap-3 border border-border px-3 py-2"
              style={{ borderRadius: "var(--radius)" }}
            >
              <span style={{ fontWeight: "var(--font-weight-medium)" }}>{bank.name}</span>
              {bank.bankId == null ? (
                // Avisos de antes da configuração por banco: não têm plafond próprio.
                <span className="text-secondary-text">{`avisos sem banco: ${formatCurrency(bank.used)}`}</span>
              ) : (
                <span className="text-secondary-text">
                  {`usado ${formatCurrency(bank.used)} de ${formatCurrency(bank.limit)} · `}
                  <span style={{ color: bank.available < 0 ? "var(--destructive)" : successColor }}>
                    {`disponível ${formatCurrency(bank.available)}`}
                  </span>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ConfirmingView() {
  const [activeTab, setActiveTab] = useState<TabId>("aps");

  const [plafond, setPlafond] = useState<ConfirmingPlafond | null>(null);
  const [payments, setPayments] = useState<ConfirmingPayment[]>([]);
  const [schedulePayments, setSchedulePayments] = useState<ConfirmingPayment[]>([]);
  const [history, setHistory] = useState<SupplierConfirmingHistory[]>([]);
  const [settings, setSettings] = useState<ConfirmingSettings | null>(null);
  const [banks, setBanks] = useState<ConfirmingBank[]>([]);
  const [bankId, setBankId] = useState<string>("");

  // Filtro inicializado a partir de ?fornecedor= (acesso vindo da ficha do fornecedor, [3.4.6]).
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(
    () => searchParams.get("fornecedor") ?? "",
  );
  const [statusFilter, setStatusFilter] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [isImportOpen, setIsImportOpen] = useState(false);

  // Modal "Novo AP".
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [eligible, setEligible] = useState<EligibleSupplierInvoice[]>([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState("");
  const [amountInput, setAmountInput] = useState("");
  const [extraInvoiceIds, setExtraInvoiceIds] = useState<number[]>([]);
  const [issueDate, setIssueDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const filters: ConfirmingPaymentFilters = useMemo(
    () => ({ statusCode: statusFilter || null }),
    [statusFilter],
  );

  const loadAll = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        // As próximas liquidações não seguem o filtro de estado da lista: são sempre todas as por liquidar.
        const [pl, pays, open, hist] = await Promise.all([
          getConfirmingPlafond(),
          getConfirmingPayments(filters),
          filters.statusCode ? getConfirmingPayments({}) : Promise.resolve(null),
          getConfirmingHistoryBySupplier(),
        ]);
        setPlafond(pl);
        setPayments(pays);
        setSchedulePayments(open ?? pays);
        setHistory(hist);
      } catch (err) {
        setError(errorMessage(err, "Não foi possível carregar o Confirming."));
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    getConfirmingSettings()
      .then(setSettings)
      .catch(() => setSettings(null));
    getConfirmingBanks()
      .then(setBanks)
      .catch(() => setBanks([]));
  }, []);

  // --- Ações sobre um AP ----------------------------------------------------
  const handleStatusChange = async (ap: ConfirmingPayment, code: string) => {
    if (busyId !== null || code === ap.statusCode) return;
    setBusyId(ap.id);
    setError(null);
    try {
      await updateConfirmingPaymentStatus(ap.id, code);
      await loadAll(true);
    } catch (err) {
      setError(errorMessage(err, "Não foi possível alterar o estado do AP."));
    } finally {
      setBusyId(null);
    }
  };

  const handleReferenceSave = async (
    ap: ConfirmingPayment,
    nextReference: string | null,
  ): Promise<boolean> => {
    if (busyId !== null) return false;
    setBusyId(ap.id);
    setError(null);
    try {
      await updateConfirmingPaymentDetails(ap.id, {
        reference: nextReference,
        issueDate: ap.issueDate,
        dueDate: ap.dueDate,
      });
      await loadAll(true);
      return true;
    } catch (err) {
      setError(errorMessage(err, "Não foi possível gravar o nº do aviso."));
      return false;
    } finally {
      setBusyId(null);
    }
  };

  // Banco escolhido por engano, ou aviso antigo ainda sem banco.
  const handleBankSave = async (ap: ConfirmingPayment, nextBankId: number) => {
    if (busyId !== null || nextBankId === ap.confirmingBankId) return;
    setBusyId(ap.id);
    setError(null);
    try {
      await updateConfirmingPaymentDetails(ap.id, {
        reference: ap.reference ?? null,
        issueDate: ap.issueDate,
        dueDate: ap.dueDate,
        confirmingBankId: nextBankId,
      });
      await loadAll(true);
    } catch (err) {
      setError(errorMessage(err, "Não foi possível mudar o banco do aviso."));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (ap: ConfirmingPayment) => {
    if (busyId !== null) return;
    if (
      !window.confirm(
        `Apagar o AP de ${ap.supplierName} (${formatCurrency(ap.amount)})? Liberta o plafond.`,
      )
    ) {
      return;
    }
    setBusyId(ap.id);
    setError(null);
    try {
      await deleteConfirmingPayment(ap.id);
      await loadAll(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        await loadAll(true);
      } else {
        setError(errorMessage(err, "Não foi possível apagar o AP."));
      }
    } finally {
      setBusyId(null);
    }
  };

  // --- Modal "Novo AP" ------------------------------------------------------
  const openCreate = async () => {
    setSelectedInvoiceId("");
    setAmountInput("");
    setExtraInvoiceIds([]);
    setIssueDate("");
    setDueDate("");
    setReference("");
    setBankId(banks.length === 1 ? String(banks[0].id) : "");
    setNotes("");
    setCreateError(null);
    setIsCreateOpen(true);
    setEligible([]);
    setEligibleLoading(true);
    try {
      setEligible(await getEligibleSupplierInvoices());
    } catch (err) {
      setCreateError(
        errorMessage(err, "Não foi possível carregar as faturas elegíveis."),
      );
    } finally {
      setEligibleLoading(false);
    }
  };

  const selectedInvoice = useMemo(
    () =>
      eligible.find(
        (inv) => String(inv.workFinancialEntryId) === selectedInvoiceId,
      ) ?? null,
    [eligible, selectedInvoiceId],
  );

  const eligibleOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      eligible.map((inv) => ({
        value: String(inv.workFinancialEntryId),
        label: inv.supplierName,
        description: `${inv.invoiceNumber ? `${inv.invoiceNumber} · ` : ""}${formatCurrency(inv.totalAmount)}`,
        keywords: inv.invoiceNumber ?? undefined,
      })),
    [eligible],
  );

  // Dias entre o banco pagar ao fornecedor e debitar a liquidação: o prazo do banco do aviso, ou o
  // prazo por omissão (Definições › Tesouraria).
  const selectedBank = banks.find((bank) => String(bank.id) === bankId) ?? null;
  const termDaysSetting = selectedBank?.termDays ?? settings?.confirmingTermDays ?? 150;

  const handleBankChange = (value: string) => {
    setBankId(value);
    const bank = banks.find((item) => String(item.id) === value);
    if (bank && issueDate) {
      setDueDate(addDaysInput(issueDate, bank.termDays));
    }
  };

  const handleSelectInvoice = (value: string) => {
    setSelectedInvoiceId(value);
    setExtraInvoiceIds([]);
    const inv = eligible.find(
      (item) => String(item.workFinancialEntryId) === value,
    );
    if (inv) {
      setAmountInput(inv.totalAmount != null ? String(inv.totalAmount) : "");
      // O banco paga ao fornecedor no vencimento da fatura (data do documento + prazo de pagamento)
      // e debita a liquidação o prazo do Confirming depois — as duas datas vêm feitas.
      const days = settings?.defaultSupplierPaymentDays ?? 30;
      const documentDay = inv.documentDate ? inv.documentDate.slice(0, 10) : todayInput();
      const supplierDay = addDaysInput(documentDay, days);
      setIssueDate(supplierDay);
      setDueDate(supplierDay ? addDaysInput(supplierDay, termDaysSetting) : "");
    } else {
      setAmountInput("");
      setIssueDate("");
      setDueDate("");
    }
  };

  const handleIssueDateChange = (value: string) => {
    setIssueDate(value);
    setDueDate(value ? addDaysInput(value, termDaysSetting) : "");
  };

  // Um aviso junta muitas vezes várias faturas do mesmo fornecedor (4 ou 5 do mesmo sub-empreiteiro).
  const sameSupplierInvoices = useMemo(
    () =>
      selectedInvoice
        ? eligible.filter(
            (inv) =>
              inv.workFinancialEntryId !== selectedInvoice.workFinancialEntryId &&
              (selectedInvoice.supplierId != null
                ? inv.supplierId === selectedInvoice.supplierId
                : inv.supplierName === selectedInvoice.supplierName),
          )
        : [],
    [eligible, selectedInvoice],
  );
  const extrasTotal = sameSupplierInvoices
    .filter((inv) => extraInvoiceIds.includes(inv.workFinancialEntryId))
    .reduce((sum, inv) => sum + (inv.totalAmount ?? 0), 0);
  const toggleExtraInvoice = (id: number) =>
    setExtraInvoiceIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );

  const rate = settings?.confirmingAnnualRatePercent ?? 0;
  // Com várias faturas, o aviso é a soma delas (não se escreve à mão).
  const amountValue =
    extraInvoiceIds.length > 0
      ? (selectedInvoice?.totalAmount ?? 0) + extrasTotal
      : parseNumber(amountInput) ?? 0;
  const preview = useMemo(
    () =>
      selectedInvoice && dueDate
        ? computeCost(amountValue, rate, issueDate || todayInput(), dueDate)
        : { termDays: 0, financialCost: 0 },
    [selectedInvoice, issueDate, dueDate, amountValue, rate],
  );
  // Com banco, o plafond desse banco; sem bancos, o plafond único.
  const available =
    (selectedBank
      ? plafond?.banks?.find((bank) => bank.bankId === selectedBank.id)?.available
      : undefined) ?? plafond?.available ?? 0;
  const overPlafond = amountValue > available;

  const handleCreate = async () => {
    if (isSaving) return;
    if (!selectedInvoice) {
      setCreateError("Selecione uma fatura de fornecedor.");
      return;
    }
    if (amountValue <= 0) {
      setCreateError("Indique um valor a financiar válido.");
      return;
    }
    if (banks.length > 0 && !selectedBank) {
      setCreateError("Indique o banco do aviso.");
      return;
    }
    if (!issueDate || !dueDate) {
      setCreateError("Indique a data de pagamento ao fornecedor e a de liquidação.");
      return;
    }
    if (dueDate < issueDate) {
      setCreateError("A liquidação não pode ser antes do pagamento ao fornecedor.");
      return;
    }
    if (overPlafond) {
      setCreateError(
        `O valor excede o plafond de Confirming disponível (${formatCurrency(available)}).`,
      );
      return;
    }
    setIsSaving(true);
    setCreateError(null);
    try {
      await createConfirmingPayment({
        workFinancialEntryId: selectedInvoice.workFinancialEntryId,
        amount: amountValue,
        issueDate,
        dueDate,
        reference: reference.trim() || null,
        additionalWorkFinancialEntryIds: extraInvoiceIds,
        confirmingBankId: selectedBank?.id ?? null,
        notes: notes.trim() || null,
      });
      setIsCreateOpen(false);
      await loadAll(true);
    } catch (err) {
      setCreateError(errorMessage(err, "Não foi possível criar o AP."));
    } finally {
      setIsSaving(false);
    }
  };

  // --- Filtro de texto (cliente) -------------------------------------------
  const normalizedFilter = filter.trim().toLowerCase();
  const shownPayments =
    normalizedFilter === ""
      ? payments
      : payments.filter((ap) =>
          [ap.supplierName, ap.workName ?? "", ap.invoiceNumber ?? ""]
            .join(" ")
            .toLowerCase()
            .includes(normalizedFilter),
        );

  const shownTotal = shownPayments.reduce((sum, ap) => sum + ap.amount, 0);
  const shownCost = shownPayments.reduce(
    (sum, ap) => sum + ap.financialCost,
    0,
  );

  const statusChips = [{ code: "", name: "Todos" }, ...STATUS_OPTIONS];

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Confirming"
        subtitle="Avisos de pagamento (reverse factoring) contra plafond bancário"
        tabs={
          <>
            <PageHeaderTab
              label="APs"
              isActive={activeTab === "aps"}
              onClick={() => setActiveTab("aps")}
            />
            <PageHeaderTab
              label="Próximas liquidações"
              isActive={activeTab === "settlements"}
              onClick={() => setActiveTab("settlements")}
            />
            <PageHeaderTab
              label="Histórico por fornecedor"
              isActive={activeTab === "history"}
              onClick={() => setActiveTab("history")}
            />
          </>
        }
      />

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        <PlafondWidget plafond={plafond} />

        {error && (
          <div
            className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        {activeTab === "aps" ? (
          <div className="space-y-4">
            <ListToolbar
              filter={filter}
              onFilterChange={setFilter}
              placeholder="Filtrar por fornecedor, obra ou fatura…"
              summary={
                <span className="text-secondary-text text-sm">
                  {shownPayments.length} AP(s) · Total{" "}
                  {formatCurrency(shownTotal)} · Custo{" "}
                  {formatCurrency(shownCost)}
                </span>
              }
            >
              <button
                type="button"
                onClick={() => setIsImportOpen(true)}
                className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] border border-border text-foreground hover:bg-accent/50 cursor-pointer text-sm"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Importar avisos do banco
              </button>
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                + Novo AP
              </button>
            </ListToolbar>

            <div className="flex items-center gap-2 flex-wrap">
              {statusChips.map((chip) => {
                const isActive = statusFilter === chip.code;
                return (
                  <button
                    key={chip.code || "all"}
                    type="button"
                    onClick={() => setStatusFilter(chip.code)}
                    className={`inline-flex items-center px-3 py-1 text-sm cursor-pointer ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "border border-border text-foreground hover:bg-accent/50"
                    }`}
                    style={{ borderRadius: "var(--radius-button)" }}
                  >
                    {chip.name}
                  </button>
                );
              })}
            </div>

            <div
              className="bg-card border border-border overflow-x-auto"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <table className="w-full" style={{ minWidth: 1100 }}>
                <thead>
                  <tr className="border-b border-border bg-muted text-left">
                    <th className="px-4 py-3">Obra</th>
                    <th className="px-4 py-3">Fornecedor</th>
                    <th className="px-4 py-3">Fatura</th>
                    <th className="px-4 py-3 text-right" style={{ width: 120 }}>
                      Valor
                    </th>
                    <th className="px-4 py-3" style={{ width: 140 }}>
                      Liquidação
                    </th>
                    <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                      Custo financeiro
                    </th>
                    <th className="px-4 py-3" style={{ width: 180 }}>
                      Estado
                    </th>
                    <th className="px-4 py-3" style={{ width: 100 }}>
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-6 text-center text-secondary-text"
                      >
                        A carregar…
                      </td>
                    </tr>
                  ) : shownPayments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-6 text-center text-secondary-text"
                      >
                        {filter !== "" || statusFilter !== ""
                          ? "Sem APs para o filtro."
                          : "Sem avisos de pagamento. Crie um AP a partir de uma fatura de fornecedor."}
                      </td>
                    </tr>
                  ) : (
                    shownPayments.map((ap) => (
                      <tr
                        key={ap.id}
                        className="border-b border-border last:border-b-0 hover:bg-accent/30"
                      >
                        <td className="px-4 py-3 break-words">
                          {ap.workName || (
                            <span className="text-secondary-text">—</span>
                          )}
                        </td>
                        <td
                          className="px-4 py-3 break-words"
                          style={{ fontWeight: "var(--font-weight-medium)" }}
                        >
                          {ap.supplierName}
                          {banks.length > 0 &&
                          ACTIVE_STATUS_CODES.includes(ap.statusCode) ? (
                            <select
                              value={ap.confirmingBankId ?? ""}
                              disabled={busyId === ap.id}
                              onChange={(event) =>
                                handleBankSave(ap, Number(event.target.value))
                              }
                              aria-label="Banco do aviso"
                              className="block mt-1 px-1 py-0.5 border border-border bg-background text-xs text-secondary-text disabled:opacity-50"
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              {ap.confirmingBankId == null && (
                                <option value="" disabled>
                                  Sem banco
                                </option>
                              )}
                              {banks.map((bank) => (
                                <option key={bank.id} value={bank.id}>
                                  {bank.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            ap.confirmingBankName && (
                              <span className="block text-xs text-secondary-text mt-0.5">
                                {ap.confirmingBankName}
                              </span>
                            )
                          )}
                        </td>
                        <td className="px-4 py-3 break-words">
                          {ap.invoiceNumber || (
                            <span className="text-secondary-text">—</span>
                          )}
                          <ApReferenceInput
                            ap={ap}
                            disabled={busyId === ap.id}
                            onSave={handleReferenceSave}
                          />
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {formatCurrency(ap.amount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatDateShort(ap.dueDate)}
                          <span className="block text-xs text-secondary-text mt-0.5">
                            {ap.settledOn
                              ? `liquidado a ${formatDateShort(ap.settledOn)}`
                              : `${ap.termDays} dia(s) desde ${formatDateShort(ap.issueDate)}`}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {formatCurrency(ap.financialCost)}
                          <span className="block text-xs text-secondary-text mt-0.5">
                            {ap.annualRatePercent}% a.a.
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={ap.statusCode}
                            disabled={busyId === ap.id}
                            onChange={(event) =>
                              handleStatusChange(ap, event.target.value)
                            }
                            aria-label="Mudar estado"
                            className={`px-2 py-1 border border-border text-sm disabled:opacity-50 ${
                              STATUS_BADGE[ap.statusCode] ??
                              "bg-background text-foreground"
                            }`}
                            style={{ borderRadius: "var(--radius)" }}
                          >
                            {STATUS_OPTIONS.map((option) => (
                              <option key={option.code} value={option.code}>
                                {option.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            className="text-destructive text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={busyId === ap.id}
                            onClick={() => handleDelete(ap)}
                          >
                            Apagar
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab === "settlements" ? (
          <SettlementsSection payments={schedulePayments} isLoading={isLoading} />
        ) : (
          <HistorySection history={history} isLoading={isLoading} />
        )}
      </div>

      {isImportOpen && (
        <ImportNoticesModal
          banks={banks}
          defaultTermDays={settings?.confirmingTermDays ?? 150}
          onClose={() => setIsImportOpen(false)}
          onCreated={() => loadAll(true)}
        />
      )}

      {isCreateOpen && (
        <Modal
          title="Novo AP"
          onClose={() => setIsCreateOpen(false)}
          maxWidthClass="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isSaving || overPlafond}
                onClick={handleCreate}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isSaving ? "A criar…" : "Criar AP"}
              </button>
            </>
          }
        >
          {createError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {createError}
            </div>
          )}

          <SearchableSelect<string>
            label="Fatura de fornecedor"
            required
            labelClassName="text-sm text-secondary-text"
            labelStyle={{}}
            panelPosition="fixed"
            value={selectedInvoiceId === "" ? null : selectedInvoiceId}
            onChange={(next) => handleSelectInvoice(next ?? "")}
            options={eligibleOptions}
            loading={eligibleLoading}
            disabled={eligibleLoading}
            placeholder={
              eligibleLoading
                ? "A carregar faturas…"
                : "Selecione uma fatura elegível"
            }
            searchPlaceholder="Procurar fatura…"
          />

          {!eligibleLoading && eligible.length === 0 && !createError && (
            <p className="text-sm text-secondary-text">
              Não há faturas de fornecedor elegíveis (todas já têm AP ou não
              existem despesas registadas).
            </p>
          )}

          {selectedInvoice && (
            <div
              className="bg-muted/30 p-3 text-sm space-y-1"
              style={{ borderRadius: "var(--radius)" }}
            >
              <div className="flex justify-between gap-2">
                <span className="text-secondary-text">Fornecedor</span>
                <span style={{ fontWeight: "var(--font-weight-medium)" }}>
                  {selectedInvoice.supplierName}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-secondary-text">Obra</span>
                <span>{selectedInvoice.workName || "—"}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-secondary-text">Total da fatura</span>
                <span>{formatCurrency(selectedInvoice.totalAmount)}</span>
              </div>
            </div>
          )}

          {selectedInvoice && sameSupplierInvoices.length > 0 && (
            <fieldset className="flex flex-col gap-1 text-sm">
              <legend className="text-secondary-text mb-1">
                Outras faturas deste fornecedor no mesmo aviso
              </legend>
              {sameSupplierInvoices.map((inv) => (
                <label
                  key={inv.workFinancialEntryId}
                  className="flex items-center gap-2 px-2 py-1 border border-border cursor-pointer"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <input
                    type="checkbox"
                    checked={extraInvoiceIds.includes(inv.workFinancialEntryId)}
                    onChange={() => toggleExtraInvoice(inv.workFinancialEntryId)}
                  />
                  <span className="flex-1">{inv.invoiceNumber ?? "Sem nº"}</span>
                  <span className="text-secondary-text">{formatDateShort(inv.documentDate ?? undefined)}</span>
                  <span>{formatCurrency(inv.totalAmount)}</span>
                </label>
              ))}
              {extraInvoiceIds.length > 0 && (
                <span className="text-xs text-secondary-text">
                  {`${extraInvoiceIds.length + 1} faturas · aviso de ${formatCurrency(amountValue)}`}
                </span>
              )}
            </fieldset>
          )}

          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Valor a financiar (€) *
            <input
              type="number"
              min="0"
              step="0.01"
              value={extraInvoiceIds.length > 0 ? amountValue.toFixed(2) : amountInput}
              disabled={extraInvoiceIds.length > 0}
              onChange={(event) => setAmountInput(event.target.value)}
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            />
          </label>

          {banks.length > 0 && (
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Banco *
              <select
                value={bankId}
                onChange={(event) => handleBankChange(event.target.value)}
                className="px-3 py-2 border border-border bg-background text-foreground text-sm"
                style={{ borderRadius: "var(--radius)" }}
              >
                <option value="">Selecionar…</option>
                {banks.map((bank) => (
                  <option key={bank.id} value={String(bank.id)}>
                    {bank.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Pagamento ao fornecedor *
            <input
              type="date"
              value={issueDate}
              onChange={(event) => handleIssueDateChange(event.target.value)}
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Liquidação ao banco *
            <input
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            />
            <span className="text-xs">
              {selectedBank && !selectedBank.isTermFixed
                ? `Pagamento ao fornecedor + ${termDaysSetting} dias por omissão — no ${selectedBank.name} o prazo varia: confirme a data na app do banco.`
                : selectedBank
                  ? `Pagamento ao fornecedor + ${termDaysSetting} dias (prazo fixo do ${selectedBank.name}).`
                  : `Pagamento ao fornecedor + ${termDaysSetting} dias (Definições › Tesouraria).`}
            </span>
          </label>

          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Nº do aviso (AP)
            <input
              type="text"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="Pode ficar para depois"
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Notas
            <textarea
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            />
          </label>

          {selectedInvoice && dueDate && (
            <div
              className="bg-muted/30 p-3 text-sm space-y-1"
              style={{ borderRadius: "var(--radius)" }}
            >
              <div className="flex justify-between gap-2">
                <span className="text-secondary-text">
                  Prazo · taxa {rate}% a.a.
                </span>
                <span>{preview.termDays} dia(s)</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-secondary-text">
                  Custo financeiro estimado
                </span>
                <span style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                  {formatCurrency(preview.financialCost)}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-secondary-text">Plafond disponível</span>
                <span
                  style={{
                    color: overPlafond ? "var(--destructive)" : undefined,
                  }}
                >
                  {formatCurrency(available)}
                </span>
              </div>
              {overPlafond && (
                <p className="text-destructive text-xs mt-1">
                  O valor excede o plafond de Confirming disponível.
                </p>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

/**
 * As liquidações que vêm aí, por dia, como na tabela do banco: o que o banco vai debitar em cada data
 * (os avisos que vencem nesse dia) — é a tesouraria de que se vai precisar.
 */
function SettlementsSection({
  payments,
  isLoading,
}: {
  payments: ConfirmingPayment[];
  isLoading: boolean;
}) {
  const open = payments
    .filter((ap) => ACTIVE_STATUS_CODES.includes(ap.statusCode))
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const byDay = open.reduce<Record<string, ConfirmingPayment[]>>((groups, ap) => {
    const day = ap.dueDate.slice(0, 10);
    (groups[day] ??= []).push(ap);
    return groups;
  }, {});
  const days = Object.keys(byDay).sort();

  if (isLoading) {
    return <div className="text-secondary-text text-sm px-1">A carregar…</div>;
  }

  if (days.length === 0) {
    return (
      <div
        className="bg-card border border-border px-4 py-6 text-center text-secondary-text"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        Sem avisos por liquidar.
      </div>
    );
  }

  return (
    <div
      className="bg-card border border-border overflow-x-auto"
      style={{ borderRadius: "var(--radius-card)" }}
    >
      <table className="w-full" style={{ minWidth: 760 }}>
        <thead>
          <tr className="border-b border-border bg-muted text-left">
            <th className="px-4 py-3" style={{ width: 130 }}>Liquidação</th>
            <th className="px-4 py-3">Banco</th>
            <th className="px-4 py-3">Nº do aviso</th>
            <th className="px-4 py-3">Fornecedor</th>
            <th className="px-4 py-3">Faturas</th>
            <th className="px-4 py-3 text-right">Valor</th>
            <th className="px-4 py-3 text-right">Custo</th>
          </tr>
        </thead>
        {days.map((day) => {
          const rows = byDay[day];
          const total = rows.reduce((sum, ap) => sum + ap.amount, 0);
          return (
            <tbody key={day}>
              {rows.map((ap, index) => (
                <tr key={ap.id} className="border-b border-border">
                  <td className="px-4 py-2 whitespace-nowrap">
                    {index === 0 ? formatDateShort(ap.dueDate) : ""}
                  </td>
                  <td className="px-4 py-2">{ap.confirmingBankName || <span className="text-secondary-text">—</span>}</td>
                  <td className="px-4 py-2">{ap.reference || <span className="text-secondary-text">—</span>}</td>
                  <td className="px-4 py-2 break-words">{ap.supplierName}</td>
                  <td className="px-4 py-2 break-words">{ap.invoiceNumber || "—"}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">{formatCurrency(ap.amount)}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap text-secondary-text">
                    {formatCurrency(ap.financialCost)}
                  </td>
                </tr>
              ))}
              <tr className="border-b border-border bg-muted/30">
                <td className="px-4 py-2" />
                <td className="px-4 py-2" colSpan={4} style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                  {`Débito previsto a ${formatDateShort(day)} (${rows.length} aviso${rows.length !== 1 ? "s" : ""})`}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                  {formatCurrency(total)}
                </td>
                <td className="px-4 py-2" />
              </tr>
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

// Histórico por fornecedor [3.4.6] — agregado por fornecedor.
function HistorySection({
  history,
  isLoading,
}: {
  history: SupplierConfirmingHistory[];
  isLoading: boolean;
}) {
  const totalAmount = history.reduce((sum, row) => sum + row.totalAmount, 0);
  const totalCost = history.reduce(
    (sum, row) => sum + row.totalFinancialCost,
    0,
  );

  return (
    <div className="space-y-4">
      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 720 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3">Fornecedor</th>
              <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                Nº de APs
              </th>
              <th className="px-4 py-3 text-right" style={{ width: 160 }}>
                Total valor
              </th>
              <th className="px-4 py-3 text-right" style={{ width: 160 }}>
                Total custo
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-6 text-center text-secondary-text"
                >
                  A carregar…
                </td>
              </tr>
            ) : history.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-6 text-center text-secondary-text"
                >
                  Sem histórico de Confirming por fornecedor.
                </td>
              </tr>
            ) : (
              history.map((row) => (
                <tr
                  key={`${row.supplierId ?? "x"}-${row.supplierName}`}
                  className="border-b border-border last:border-b-0 hover:bg-accent/30"
                >
                  <td
                    className="px-4 py-3 break-words"
                    style={{ fontWeight: "var(--font-weight-medium)" }}
                  >
                    {row.supplierName}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {row.count}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatCurrency(row.totalAmount)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatCurrency(row.totalFinancialCost)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {history.length > 0 && (
            <tfoot>
              <tr
                className="border-t border-border bg-muted"
                style={{ fontWeight: "var(--font-weight-semi-bold)" }}
              >
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {history.reduce((sum, row) => sum + row.count, 0)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {formatCurrency(totalAmount)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {formatCurrency(totalCost)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
